// Prompt ที่เอาไปวางในโปรเจคปลายทาง เพื่อให้ระบบนั้นแสดงเมนูนี้ได้
//
// ข้อความต้องตรงกับของเดิมทุกบรรทัด เพราะเป็นสิ่งที่ส่งให้ทีมอื่นไปทำจริง
import type { GlobalMenuItem } from "@contracts/api";

export type PromptKind = "script" | "push";

export function buildPrompt(kind: PromptKind, items: GlobalMenuItem[], origin: string): string {
  const enabled = items.filter((m) => m.enabled);
  const menu = enabled
    .map((m) => `- ${m.label} — ${m.description || "(ไม่มีคำอธิบาย)"} → ${m.url}`)
    .join("\n");
  const list = menu || "(ยังไม่มีเมนู)";

  if (kind === "script") {
    // แยก </script> เป็นชิ้นเพื่อไม่ให้ปิด tag ของหน้าเว็บที่ฝัง prompt นี้ไว้
    const closeTag = "</" + "script>";
    return `เพิ่มเมนู 3 ขีด (Global Menu) ที่ลิงก์ไปยังระบบในเครือ ให้กับโปรเจคนี้

วิธีที่เร็วที่สุด — แปะสคริปต์บรรทัดเดียวก่อน </body> ของทุกหน้า:

    <script src="${origin}/global-menu.js" defer>${closeTag}

สคริปต์นี้จะ:
- สร้างปุ่ม 3 ขีด + ลิ้นชักเมนูที่สไลด์จากซ้าย พร้อม CSS ในตัว (ไม่ต้องเพิ่ม CSS)
- ฝังรายการเมนูมาในไฟล์แล้ว → ไม่มีการเรียก API ตอน runtime และ browser cache ให้ 5 นาที
- ถ้าหน้ามี element ที่มี attribute [data-global-menu] จะไปวางปุ่มในนั้น
  ไม่งั้นปุ่มจะลอยที่มุมซ้ายบน — แนะนำให้ใส่ที่หัวแถบ navbar ของโปรเจค เช่น
      <span data-global-menu></span>
- เปิด/ปิดด้วยโค้ดได้ทาง window.globalMenu.open() / .close()

ข้อกำหนด:
- ห้ามแก้รายการเมนูเองในโปรเจคนี้ — เมนูมีเจ้าของเดียวคือ Agency Intelligence
  (แก้ที่ Setting → Global Menu แล้วสคริปต์จะอัปเดตให้เองภายใน 5 นาที)
- ถ้าโปรเจคมี CSP ต้องอนุญาต script-src จาก ${origin}
- ถ้าไม่ต้องการพึ่งโดเมนภายนอกตอน runtime ให้ใช้แบบที่ 2 (รับสำเนาผ่าน Push) แทน

เมนูที่จะแสดงตอนนี้ (${enabled.length} รายการ):
${list}`;
  }

  return `เพิ่มเมนู 3 ขีด (Global Menu) ที่ลิงก์ไปยังระบบในเครือ ให้กับโปรเจคนี้
โดย "ไม่" พึ่งโดเมนภายนอกตอน runtime — เก็บสำเนาเมนูไว้ในระบบตัวเอง

สิ่งที่ต้องทำ 3 อย่าง:

1) endpoint รับสำเนาเมนู
   POST /api/global-menu
   - header: X-Global-Menu-Secret: <secret ที่ตกลงกัน> → ไม่ตรงให้ตอบ 401
   - body: {"version":"<เวลาที่แก้ล่าสุด>","items":[{"label":"...","description":"...","url":"https://..."}]}
   - เก็บทั้งก้อนลง storage ของโปรเจค (ตาราง 1 แถว / ไฟล์ JSON / KV ก็ได้) แล้วตอบ 200
   - เขียนทับของเดิมทุกครั้ง ไม่ต้อง merge — ต้นทางส่งมาครบทุกครั้ง

2) แสดงเมนูจากสำเนาที่เก็บไว้
   - ปุ่ม 3 ขีดที่หัวแถบ navbar → คลิกแล้วเปิดลิ้นชักสไลด์จากซ้าย
   - ในลิ้นชัก: หัวข้อ "ระบบในเครือ" แล้วไล่รายการ แต่ละอันเป็นลิงก์
     บรรทัดแรก = label (ตัวหนา สีลิงก์) บรรทัดสอง = description (ตัวเล็ก สีเทา)
   - รายการที่ url ชี้กลับมาที่ host ตัวเอง ให้แสดงเป็นสีปกติ ไม่ใช่สีลิงก์ (บอกว่าอยู่ที่นี่)
   - ถ้ายังไม่เคยได้รับ push ให้ซ่อนปุ่มไปเลย ดีกว่าโชว์ลิ้นชักเปล่า
   - ต้นแบบหน้าตา + CSS ดูได้จาก ${origin}/global-menu.js (ลอกไปปรับได้)

3) แจ้ง URL ของ endpoint กลับมา
   เอาไปใส่ที่ Agency Intelligence → Setting → Global Menu → ระบบปลายทาง
   พร้อม secret เดียวกัน แล้วกดปุ่ม "Push ไปทุกระบบ" เพื่อทดสอบ

ข้อกำหนด:
- ห้าม hardcode รายการเมนูในโปรเจคนี้ ให้มาจาก push เท่านั้น (เมนูมีเจ้าของเดียว)
- ห้ามแก้ /api prefix หรือ route เดิมของโปรเจค — เพิ่ม route ใหม่เท่านั้น
- ถ้าหน้าเว็บ render ฝั่ง server ให้ฝังเมนูลง HTML ตอน render ไม่ต้อง fetch จาก browser

ข้อมูลที่จะได้รับตอนนี้ (${enabled.length} รายการ):
${list}

ตัวอย่าง payload จริง ดูได้ที่ ${origin}/app-api/global-menu`;
}
